/**
 * Public API for extracting legend entries from MapLibre style layers.
 *
 * Entry points:
 *   - `extractLegendEntries`     — convert an array of style layers → LegendEntry[].
 *   - `extractLegendEntry`       — convert a single layer → LegendEntry | null.
 *
 * Internal flow:
 *   extractLegendEntries          groups GeoServer :N suffixed layers
 *     ├─ extractSingleLayer       dispatches fill/line/circle → extractLegendEntry
 *     │                           or symbol → extractSpriteLegendEntry
 *     │                           or sprite-sheet fallback → SpriteLegendEntry
 *     └─ buildCompositeEntry      builds CompositeLegendEntry from :0/:1/:2+ layers
 */

import type { LayerSpecification } from "maplibre-gl";

import type {
  FillCompositeLegendEntry,
  LegendEntry,
  LineCompositeLegendEntry,
  SpriteLegendEntry,
} from "../types/legend-types";

import { evalColor, evalNumber } from "./evaluate-expression";
import { extractLegendEntry } from "./extract-paint-entry";
import { extractSpriteLegendEntry } from "./extract-sprite-entry";
import {
  applyOpacityToColor,
  getLayerLabel,
  getPatternPaint,
  getSourceLayer,
  isExpression,
} from "./layer-utils";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Converts an array of MapLibre style layers into legend entries.
 *
 * GeoServer-generated styles emit multiple layers per symbolizer rule, suffixed
 * with `:0`, `:1`, `:2` etc.  These are automatically grouped and merged into a
 * single `CompositeLegendEntry` per rule.  All other layers are processed individually.
 *
 * GeoServer rule structure:
 *   `:0`  — background fill / base layer
 *   `:1`  — outline (line layer)
 *   `:2+` — pattern overlay (fill-pattern or line-pattern)
 *
 * @param layers - The `layers` array from a MapLibre StyleSpecification, already
 *   filtered to the subset that should appear in the legend.
 */
export function extractLegendEntries(layers: LayerSpecification[]): LegendEntry[] {
  const plain = layers.filter((l) => !/:\d+$/.test(l.id));

  const groups = new Map<string, LayerSpecification[]>();
  for (const layer of layers) {
    if (!/:\d+$/.test(layer.id)) continue;
    const prefix = layer.id.replace(/:\d+$/, "");
    const group = groups.get(prefix) ?? [];
    if (group.length === 0) groups.set(prefix, group);
    group.push(layer);
  }

  const result: LegendEntry[] = [];

  for (const layer of plain) {
    const entry = extractSingleLayer(layer);
    if (entry) result.push(entry);
  }

  for (const group of groups.values()) {
    group.sort((a, b) => {
      const ai = parseInt(a.id.match(/:(\d+)$/)?.[1] ?? "0");
      const bi = parseInt(b.id.match(/:(\d+)$/)?.[1] ?? "0");
      return ai - bi;
    });

    if (group.length === 1) {
      const entry = extractSingleLayer(group[0]);
      if (entry) result.push(entry);
      continue;
    }

    const composite = buildCompositeEntry(group);
    if (composite) result.push(composite);
  }

  return result;
}

/**
 * Builds a CompositeLegendEntry from a sorted group of GeoServer symbolizer layers.
 * Falls back to processing the base layer (:0) individually if no useful data is found.
 */
function buildCompositeEntry(group: LayerSpecification[]): LegendEntry | null {
  const base = group[0]; // :0 → background fill
  const lineLayer = group.find((l) => l.type === "line"); // :1 → outline
  const symbolLayer = group.find((l) => l.type === "symbol");
  const patternLayer = group.find((l) => getPatternPaint(l) !== null); // :2+ → pattern overlay

  const spriteEntry = symbolLayer ? extractSpriteLegendEntry(symbolLayer) : null;
  const firstIcon = spriteEntry?.kind === "sprite" ? spriteEntry.icons[0] : undefined;

  const rawFillColor = base.type === "fill" ? evalColor(base.paint?.["fill-color"]) : undefined;
  const fillOpacity = base.type === "fill" ? evalNumber(base.paint?.["fill-opacity"]) ?? 1 : 1;
  const fillColor = rawFillColor ? applyOpacityToColor(rawFillColor, fillOpacity) : undefined;

  const fillOutlineColor =
    base.type === "fill" ? evalColor(base.paint?.["fill-outline-color"]) : undefined;

  const rawLineColor =
    lineLayer?.type === "line" ? evalColor(lineLayer.paint?.["line-color"]) : undefined;
  const lineOpacity =
    lineLayer?.type === "line" ? evalNumber(lineLayer.paint?.["line-opacity"]) ?? 1 : 1;
  const resolvedLineColor = rawLineColor
    ? applyOpacityToColor(rawLineColor, lineOpacity)
    : undefined;

  const resolvedLineWidth =
    lineLayer?.type === "line" ? evalNumber(lineLayer.paint?.["line-width"]) : undefined;

  // Only treat as dasharray if it's a plain number array, not an expression.
  const rawDasharray = lineLayer?.type === "line" ? lineLayer.paint?.["line-dasharray"] : undefined;
  const resolvedLineDasharray =
    Array.isArray(rawDasharray) && !isExpression(rawDasharray)
      ? (rawDasharray as number[])
      : undefined;

  const patternIconName = patternLayer ? getPatternPaint(patternLayer) ?? undefined : undefined;

  const hasFill = !!fillColor || !!patternIconName || !!fillOutlineColor;

  // If there's no fill, no outline, and no symbol, fall back to processing the base layer individually.
  if (!hasFill && !resolvedLineColor && !firstIcon?.iconName) {
    return extractSingleLayer(base);
  }

  const base_ = {
    layerId: base.id,
    layerLabel: getLayerLabel(base),
    sourceLayer: getSourceLayer(base),
  };

  if (hasFill) {
    const composite: FillCompositeLegendEntry = {
      kind: "composite-fill",
      ...base_,
      fillColor,
      fillOutlineColor,
      outlineColor: resolvedLineColor,
      outlineWidth: resolvedLineWidth,
      outlineDasharray: resolvedLineDasharray,
      patternIconName,
      pointIconName: firstIcon?.iconName,
      pointRotation: firstIcon?.rotation,
    };
    return composite;
  }

  const composite: LineCompositeLegendEntry = {
    kind: "composite-line",
    ...base_,
    lineColor: resolvedLineColor ?? "",
    lineWidth: resolvedLineWidth,
    lineDasharray: resolvedLineDasharray,
    pointIconName: firstIcon?.iconName,
    pointRotation: firstIcon?.rotation,
  };
  return composite;
}

// ---------------------------------------------------------------------------
// Single-layer dispatcher
// ---------------------------------------------------------------------------

/**
 * Converts a single layer to a LegendEntry, trying in order:
 *   1. Symbol layer → SpriteLegendEntry via icon-image.
 *   2. fill-pattern / line-pattern → SpriteLegendEntry.
 *   3. Paint color expression → StaticLegendEntry / MatchLegendEntry / etc.
 */
function extractSingleLayer(layer: LayerSpecification): LegendEntry | null {
  if (layer.type === "symbol") return extractSpriteLegendEntry(layer);

  const patternIconName = getPatternPaint(layer);
  if (patternIconName) {
    return buildSpriteEntry(layer, patternIconName);
  }

  const staticLegendEntry = extractLegendEntry(layer);
  if (staticLegendEntry) {
    return staticLegendEntry;
  }

  console.warn("No legend entry could be extracted for layer", layer);

  return null;
}

function buildSpriteEntry(layer: LayerSpecification, iconName: string): SpriteLegendEntry {
  const label = getLayerLabel(layer);
  return {
    kind: "sprite",
    layerId: layer.id,
    layerLabel: label,
    sourceLayer: getSourceLayer(layer),
    icons: [{ label, iconName }],
  };
}
